import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Brain, Loader } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import { LetterGrid } from '../components/LetterGrid';
import { SessionTimer } from '../components/SessionTimer';
import { SessionStatus } from '../components/SessionStatus';
import { SummaryModal } from '../components/SummaryModal';
import { ExportButton } from '../components/ExportButton';
import { useLanguage } from '../lib/i18n/context';
import type { Entry, Session, AISummary } from '../lib/types';

export function ResultPage() {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguage();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [summary, setSummary] = useState<AISummary | null>(null);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      navigate('/');
      return;
    }

    const loadSession = async () => {
      try {
        // Load session by share token
        const { data: sessionData, error: sessionError } = await supabase
          .from('sessions')
          .select('*')
          .eq('share_token', token)
          .single();

        if (sessionError) {
          throw sessionError;
        }
        
        if (!sessionData) {
          throw new Error(t('session.invalid'));
        }
        
        if (!sessionData.is_active) {
          throw new Error(t('session.invalid'));
        }
        
        if (new Date(sessionData.expires_at) < new Date()) {
          throw new Error(t('session.expired'));
        }

        setSession(sessionData);

        // Load entries
        const { data: entriesData, error: entriesError } = await supabase
          .from('entries')
          .select('*')
          .eq('session_id', sessionData.id);

        if (entriesError) throw entriesError;
        setEntries(entriesData || []);

        // Load existing summary if available
        const { data: summaryData } = await supabase
          .from('ai_summaries')
          .select('*')
          .eq('session_id', sessionData.id)
          .eq('payment_status', 'completed')
          .maybeSingle();
        
        if (summaryData) {
          setSummary(summaryData);
        }

        setIsLoading(false);
      } catch (error) {
        console.error('Error loading session:', error);
        setError(error instanceof Error ? error.message : t('common.error'));
        setIsLoading(false);
      }
    };

    loadSession();

    // Set up real-time subscription for entries
    const entriesSubscription = supabase
      .channel('entries-result')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'entries' },
        (payload) => {
          if (payload.new && session?.id === (payload.new as Entry).session_id) {
            setEntries(current => [...current, payload.new as Entry]);
          }
        }
      )
      .subscribe();

    return () => {
      entriesSubscription.unsubscribe();
    };
  }, [token, navigate, session?.id, t]);

  const handleRequestSummary = async (forceNew = false) => {
    if (!session || entries.length < 5) return;
    
    setShowSummaryModal(true);
    
    // If we already have a completed summary and don't want to force a new one,
    // just show the existing one without making an API call
    if (summary?.payment_status === 'completed' && !forceNew) {
      return;
    }

    setIsSummaryLoading(true);
    setSummaryError(null);
    
    try {
      // Check if a summary already exists
      const { data: existingSummary, error: existingError } = await supabase
        .from('ai_summaries')
        .select('*')
        .eq('session_id', session.id)
        .maybeSingle();

      if (existingError) throw existingError;

      if (existingSummary && !forceNew) {
        if (existingSummary.payment_status === 'completed') {
          setSummary(existingSummary);
          setIsSummaryLoading(false);
          return;
        }
      }

      // Create new summary or update existing one
      const operation = existingSummary 
        ? supabase.from('ai_summaries').update({ payment_status: 'pending' }).eq('id', existingSummary.id)
        : supabase.from('ai_summaries').insert({
            session_id: session.id,
            content: '', // Will be filled by the backend
            payment_status: 'pending'
          });

      const { error } = await operation;
      if (error) throw error;
      
      // Check for updated summary after a delay
      setTimeout(async () => {
        const { data: updatedSummary, error: updatedError } = await supabase
          .from('ai_summaries')
          .select('*')
          .eq('session_id', session.id)
          .eq('payment_status', 'completed')
          .maybeSingle();
        
        if (!updatedError && updatedSummary) {
          setSummary(updatedSummary);
        }
        setIsSummaryLoading(false);
      }, 5000);
      
    } catch (error) {
      console.error('Error requesting summary:', error);
      setSummaryError(error instanceof Error ? error.message : t('analysis.error'));
      setIsSummaryLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="text-center">
          <Loader className="mx-auto h-12 w-12 animate-spin text-primary-500" />
          <p className="mt-4 text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="mx-auto max-w-md text-center">
          <Brain className="mx-auto h-16 w-16 text-gray-400" />
          <h2 className="mt-4 text-xl font-semibold text-gray-900">
            {error || t('session.invalid')}
          </h2>
          <p className="mt-2 text-gray-600">
            {t('error.404.message')}
          </p>
          <button
            onClick={() => navigate('/')}
            className="mt-6 rounded-lg bg-primary-500 px-6 py-2 text-white hover:bg-primary-600 transition-colors"
          >
            {t('common.home')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {session.topic}
          </h1>
          <p className="text-sm text-accent-600 mt-1">
            {t('session.readonly')}
          </p>
        </div>
        <div className="flex flex-col gap-2">
          <SessionTimer
            startTime={session.created_at}
            expiresAt={session.expires_at}
          />
          <ExportButton 
            entries={entries}
            summary={summary}
            topic={session.topic}
          />
        </div>
      </div>

      <SessionStatus
        entries={entries}
        participants={[]}
        maxParticipants={session.max_participants}
        onRequestSummary={handleRequestSummary}
        isViewOnly={true}
        summary={summary}
        sessionId={session.id}
      />

      <div className="grid gap-6">
        <LetterGrid
          sessionId={session.id}
          participant={null}
          entries={entries}
          participants={[]}
          onNewEntry={() => {}}
          onInputFocus={() => setIsInputFocused(true)}
          onInputBlur={() => setIsInputFocused(false)}
          isViewOnly={true}
        />
      </div>

      {showSummaryModal && (
        <SummaryModal
          summary={summary}
          isLoading={isSummaryLoading}
          error={summaryError}
          onClose={() => setShowSummaryModal(false)}
        />
      )}
    </div>
  );
}